import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { superAdminAPI } from '../services/api';

const SuperAdminContext = createContext(null);

export const useSuperAdmin = () => {
  const context = useContext(SuperAdminContext);
  if (!context) {
    throw new Error('useSuperAdmin must be used within a SuperAdminProvider');
  }
  return context;
};

export const SuperAdminProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if super admin is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('superAdminToken');
    const savedUser = localStorage.getItem('superAdminUser');

    if (token && savedUser) {
      try {
        // Verify token is still valid
        const response = await superAdminAPI.getProfile();
        const userData = response.data?.data || response.data;

        setAdminUser(userData);
        setIsAdmin(true);
      } catch (error) {
        // Token expired or invalid
        logout();
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await superAdminAPI.login({ email, password });
      const { token, user: userData } = response.data?.data || response.data;

      localStorage.setItem('superAdminToken', token);
      localStorage.setItem('superAdminUser', JSON.stringify(userData));

      setAdminUser(userData);
      setIsAdmin(true);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      return { success: false, message };
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdminUser');
    setAdminUser(null);
    setIsAdmin(false);
  }, []);

  const value = {
    adminUser,
    loading,
    isAdmin,
    login,
    logout,
    checkAuth,
  };

  return (
    <SuperAdminContext.Provider value={value}>
      {children}
    </SuperAdminContext.Provider>
  );
};

export default SuperAdminContext;
